import { useState, useMemo, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRememberFilters } from "@/hooks/useRememberFilters";
import { useNavigate } from "react-router-dom";
import { TrailerBolPreviewDialog } from "@/components/trailers/TrailerBolPreviewDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { TrailerTypesTab } from "@/components/trailers/TrailerTypesTab";
import { Truck, Settings } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { useTenant } from "@/hooks/useTenant";
import { ExpandingSearch } from "@/components/ui/expanding-search";
import { SearchChip } from "@/components/ui/search-chip";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTrailerTypes, getTrailerTypeLabel } from "@/hooks/useTrailerTypes";
import { format } from "date-fns";
import { DwellTimeChip } from "@/components/yard/DwellTimeChip";
import { Clock } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkActionBar } from "@/components/ui/bulk-action-bar";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { SortableHeader } from "@/components/ui/sortable-header";
import type { SortDirection } from "@/hooks/useTableSort";
import { TrailerDetailDialog } from "@/components/yard-view/TrailerDetailDialog";
import { useUrlFilterState, useUrlArrayFilterState } from "@/hooks/useUrlFilterState";
import { TrailerFilterBar } from "@/components/trailers/TrailerFilterBar";
import {
  LoadStatusCell,
  TrailerAssociatedOrdersCell,
  TrailerDocumentsCell,
  TrailerDwellCell,
  TrailerLocationCell,
  TrailerRecentEventCell,
  TrailerStatusCell,
} from "@/components/trailers/TrailerRowCells";
import {
  DOCS_FILTER_OPTIONS,
  DWELL_FILTER_OPTIONS,
  EVENT_FILTER_OPTIONS,
  EVENT_TYPE_LABELS,
  LOCATION_STATUS_LABELS,
  SERVICE_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
  toArrayOrNull,
  type TrailerFilterKey,
  type TrailerFilterOption,
} from "@/lib/trailer-filters";
import { useTrailerImageUrls } from "@/lib/trailer-image";
import { ResponsiveTableShell } from "@/components/ui/responsive-table-shell";
import { TablePagination } from "@/components/ui/table-pagination";
import { TrailerCard } from "@/components/trailers/TrailerCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Search as SearchIcon } from "lucide-react";

const DEFAULT_TRAILERS_PAGE_SIZE = 50;
const TRAILER_PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const ALL_FILTER_KEYS: readonly TrailerFilterKey[] = [
  "type", "scac", "status", "dwell", "event", "docs", "service", "location",
];

export default function Trailers() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  useRememberFilters();
  const [searchQuery, setSearchQuery] = useUrlFilterState<string>("q", "");
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const [selectedTrailer, setSelectedTrailer] = useState<any>(null);
  const { data: trailerTypesData } = useTrailerTypes();
  const { roles } = useUserRole();
  const canManageTypes = roles.includes("admin") || roles.includes("dispatcher");
  const [activeTab, setActiveTab] = useUrlFilterState<"trailers" | "types">("tab", "trailers");

  // Filter state
  const [typeFilter, setTypeFilter] = useUrlArrayFilterState("type");
  const [scacFilter, setScacFilter] = useUrlArrayFilterState("scac");
  const [statusFilter, setStatusFilter] = useUrlArrayFilterState("status");
  const [dwellFilter, setDwellFilter] = useUrlArrayFilterState("dwell");
  const [eventFilter, setEventFilter] = useUrlArrayFilterState("event");
  const [docsFilter, setDocsFilter] = useUrlArrayFilterState("docs");
  const [serviceFilter, setServiceFilter] = useUrlArrayFilterState("service");
  const [locationFilter, setLocationFilter] = useUrlArrayFilterState("location");
  // Server-driven sort + pagination
  const [sort, setSort] = useState<{ key: string | null; direction: SortDirection }>(
    { key: "trailer_id", direction: "asc" },
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState<number>(DEFAULT_TRAILERS_PAGE_SIZE);
  // Legacy footer is fixed at 50 rows/page; only the unified footer can change it.
  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setPage(1);
  }, []);
  const toggleSort = useCallback((key: string) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return { key: "trailer_id", direction: "asc" };
    });
    setPage(1);
  }, []);

  // Any filter/search change resets page.
  useEffect(() => { setPage(1); }, [
    debouncedSearch,
    typeFilter, scacFilter, statusFilter, dwellFilter, eventFilter, docsFilter, serviceFilter, locationFilter,
  ]);

  const filterValues: Partial<Record<TrailerFilterKey, string[]>> = {
    type: typeFilter,
    scac: scacFilter,
    status: statusFilter,
    dwell: dwellFilter,
    event: eventFilter,
    docs: docsFilter,
    service: serviceFilter,
    location: locationFilter,
  };

  const setFilter = useCallback((key: TrailerFilterKey, value: string[]) => {
    const setters: Record<TrailerFilterKey, (v: string[]) => void> = {
      type: setTypeFilter,
      scac: setScacFilter,
      status: setStatusFilter,
      dwell: setDwellFilter,
      event: setEventFilter,
      docs: setDocsFilter,
      service: setServiceFilter,
      location: setLocationFilter,
    };
    setters[key]?.(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: pageData, isLoading, isFetching } = useQuery({
    queryKey: [
      "trailers-page",
      tenantId,
      debouncedSearch.trim(),
      typeFilter, scacFilter, statusFilter, dwellFilter, eventFilter, docsFilter, serviceFilter, locationFilter,
      sort.key, sort.direction, page, pageSize,
    ],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("list_trailers_page" as any, {
        p_tenant_id: tenantId!,
        p_search: debouncedSearch.trim() || null,
        p_type_ids: toArrayOrNull(typeFilter),
        p_scacs: toArrayOrNull(scacFilter),
        p_statuses: toArrayOrNull(statusFilter),
        p_dwell: toArrayOrNull(dwellFilter),
        p_event: toArrayOrNull(eventFilter),
        p_docs: toArrayOrNull(docsFilter),
        p_location_ids: toArrayOrNull(locationFilter),
        p_service: toArrayOrNull(serviceFilter),
        p_sort: sort.key ?? "trailer_id",
        p_dir: sort.direction ?? "asc",
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
      });
      if (error) throw error;
      const row = (data as any[])?.[0];
      return {
        rows: (row?.rows ?? []) as any[],
        totalCount: Number(row?.total_count ?? 0),
      };
    },
    placeholderData: (prev) => prev,
  });
  const trailers = pageData?.rows ?? [];
  const totalCount = pageData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  // Pending = user has typed but the debounced query hasn't caught up, OR the
  // network request itself is inflight. Either way we swap the table body for
  // skeleton rows so it's obvious a search is happening.
  const isSearchPending = searchQuery.trim() !== debouncedSearch.trim();
  const isBusy = isFetching || isSearchPending;
  const showSkeleton = isBusy && !isLoading;

  const { data: allLocations } = useQuery({
    queryKey: ["locations-short-codes"],
    queryFn: async () => {
      const { data, error } = await supabase.from("locations").select("id, short_code, city").eq("archived", false);
      if (error) throw error;
      return data;
    },
  });

  // Tenant-wide SCAC list for the SCAC filter dropdown (independent of the visible page).
  const { data: scacDropdownRows } = useQuery({
    queryKey: ["trailer-scacs", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "list_trailer_scacs_for_tenant" as any,
        { p_tenant_id: tenantId! },
      );
      if (error) throw error;
      return (data as { scac: string }[]) ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const deleteTrailerMutation = useMutation({
    mutationFn: async (trailerId: string) => {
      const { error: eventsError } = await supabase.from("trailer_events").delete().eq("trailer_id", trailerId);
      if (eventsError) throw eventsError;
      const { error: commentsError } = await supabase.from("trailer_comments").delete().eq("trailer_id", trailerId);
      if (commentsError) throw commentsError;
      const { error } = await supabase.from("trailers").delete().eq("id", trailerId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trailers-page"] });
      toast.success("Trailer deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        const { error: eventsError } = await supabase.from("trailer_events").delete().eq("trailer_id", id);
        if (eventsError) throw eventsError;
        const { error: commentsError } = await supabase.from("trailer_comments").delete().eq("trailer_id", id);
        if (commentsError) throw commentsError;
      }
      const { error } = await supabase.from("trailers").delete().in("id", ids);
      if (error) throw error;
      return ids.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["trailers-page"] });
      toast.success(`Deleted ${count} trailer(s)`);
      clear();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Pre-resolve signed URLs for every trailer image. The
  // `trailer-images` bucket is private, so we sign on the client and
  // cache by `image_path`.
  const trailerImageUrls = useTrailerImageUrls(
    (trailers ?? []).map((t: any) => t.image_path),
  );
  const getImageUrl = (imagePath: string | null) => {
    if (!imagePath) return null;
    return trailerImageUrls[imagePath] ?? null;
  };

  // Derive dynamic filter options
  const typeOptions = useMemo(() =>
    (trailerTypesData ?? []).map((t) => ({ label: t.label, value: t.id })),
    [trailerTypesData]
  );

  const scacOptions = useMemo(() =>
    (scacDropdownRows ?? []).map((r) => ({ label: r.scac, value: r.scac })),
    [scacDropdownRows],
  );

  const { selectedIds, toggle, toggleAll, clear, allSelected, someSelected, count } = useBulkSelection(trailers);

  const locationOptions = useMemo(() =>
    [...((allLocations ?? []) as { id: string; short_code: string; city: string | null }[])]
      .filter((l) => l.short_code)
      .sort((a, b) => a.short_code.localeCompare(b.short_code))
      .map((l) => ({ label: l.city ? `${l.short_code}: ${l.city}` : l.short_code, value: l.id })),
    [allLocations],
  );

  const filterOptions: Partial<Record<TrailerFilterKey, TrailerFilterOption[]>> = {
    type: typeOptions,
    scac: scacOptions,
    status: STATUS_FILTER_OPTIONS,
    dwell: DWELL_FILTER_OPTIONS,
    event: EVENT_FILTER_OPTIONS,
    docs: DOCS_FILTER_OPTIONS,
    service: SERVICE_FILTER_OPTIONS,
    location: locationOptions,
  };

  const resetAllFilters = () => {
    setTypeFilter(["all"]);
    setScacFilter(["all"]);
    setStatusFilter(["all"]);
    setDwellFilter(["all"]);
    setEventFilter(["all"]);
    setDocsFilter(["all"]);
    setServiceFilter(["all"]);
    setLocationFilter(["all"]);
    setSearchQuery("");
    setPage(1);
  };

  const hasActiveFilter =
    !!searchQuery.trim() ||
    ALL_FILTER_KEYS.some((k) => toArrayOrNull(filterValues[k]) !== null);

  const tabItems = [
    { key: "trailers" as const, label: "Trailers", icon: <Truck className="h-4 w-4" /> },
    ...(canManageTypes ? [{ key: "types" as const, label: "Types", icon: <Settings className="h-4 w-4" /> }] : []),
  ];

  return (
    <AppLayout
      title="Trailers"
      subtitle={
        canManageTypes ? (
          <nav className="flex gap-6 -mb-px">
            {tabItems.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        ) : undefined
      }
    >
      {activeTab === "types" && canManageTypes ? (
        <TrailerTypesTab />
      ) : (
      <>
      <div className="space-y-4 animate-fade-in" >
        <TrailerFilterBar
          keys={ALL_FILTER_KEYS}
          values={filterValues}
          options={filterOptions}
          onChange={setFilter}
          onResetAll={resetAllFilters}
          leadingSlot={<SearchChip query={searchQuery} onClear={() => setSearchQuery("")} />}
          trailingSlot={
            <>
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {isBusy ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Searching…
                  </span>
                ) : (
                  <>
                    {totalCount} trailer{totalCount === 1 ? "" : "s"}
                  </>
                )}
              </span>
              <ExpandingSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search trailers..." />
            </>
          }
        />

        <div>
          <BulkActionBar count={count} onClear={clear} entityLabel="trailer">
            <Button
              variant="ghost"
              size="sm"
              className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground"
              onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
              disabled={bulkDeleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </BulkActionBar>
          <div className={`rounded-lg border bg-card ${count > 0 ? "rounded-t-none border-t-0" : ""}`}>
          <ResponsiveTableShell
            cards={
              <div className="flex flex-col gap-3 p-3">
                {isLoading || showSkeleton ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : trailers.length ? (
                  trailers.map((trailer: any) => (
                    <TrailerCard
                      key={trailer.id}
                      trailer={trailer}
                      typeLabel={getTrailerTypeLabel(trailer.trailer_type_id, trailerTypesData) ?? "Unknown"}
                      allLocations={allLocations as any}
                      imageUrl={getImageUrl(trailer.image_path)}
                      onClick={setSelectedTrailer}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    {hasActiveFilter ? "No trailers match your filter criteria" : "No trailers found"}
                  </div>
                )}
              </div>
            }
            table={
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                    {...(someSelected ? { "data-state": "indeterminate" } : {})}
                  />
                </TableHead>
                
                <TableHead><SortableHeader label="Trailer ID" sortKey="trailer_id" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
                <TableHead><SortableHeader label="Type" sortKey="type" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
                <TableHead>Load Status</TableHead>
                <TableHead><SortableHeader label="SCAC" sortKey="scac" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
                <TableHead><SortableHeader label="Location" sortKey="location" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
                <TableHead><SortableHeader label="Status" sortKey="status" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
                <TableHead><SortableHeader label="Dwell Time" sortKey="dwell" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
                <TableHead><SortableHeader label="Most Recent Event" sortKey="event" currentKey={sort.key} currentDirection={sort.direction} onToggle={toggleSort} /></TableHead>
                <TableHead>Associated Orders</TableHead>
                <TableHead>Documents</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : showSkeleton ? (
                Array.from({ length: Math.min(pageSize, Math.max(trailers.length || 8, 8)) }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    <TableCell className="w-[40px]"><Skeleton className="h-4 w-4" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                    <TableCell className="w-[60px]"></TableCell>
                  </TableRow>
                ))
              ) : trailers.length ? (
                trailers.map((trailer: any) => {
                  const event = trailer.latest_event ?? null;
                  const orders = (trailer.associated_orders ?? []) as any[];
                  const docCount = trailer.doc_count ?? 0;
                  return (
                    <TableRow key={trailer.id} className="cursor-pointer" onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button, input, a, [role="checkbox"]')) return;
                      setSelectedTrailer(trailer);
                    }}>
                      <TableCell className="w-[40px]">
                        <Checkbox
                          checked={selectedIds.has(trailer.id)}
                          onCheckedChange={() => toggle(trailer.id)}
                        />
                      </TableCell>
                      <TableCell><span className="font-medium">{trailer.trailer_id}</span></TableCell>
                      <TableCell className="whitespace-nowrap">{getTrailerTypeLabel(trailer.trailer_type_id, trailerTypesData) ?? "Unknown"}</TableCell>
                      <TableCell><LoadStatusCell loadStatus={trailer.load_status} /></TableCell>
                      <TableCell>{trailer.scac ?? "—"}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        <TrailerLocationCell trailer={trailer} allLocations={allLocations as any} />
                      </TableCell>
                      <TableCell><TrailerStatusCell status={trailer.current_location_status} sinceIso={trailer.latest_event?.occurred_at} /></TableCell>
                      <TableCell className="whitespace-nowrap"><TrailerDwellCell trailer={trailer} /></TableCell>
                      <TableCell className="whitespace-nowrap"><TrailerRecentEventCell event={trailer.latest_event} /></TableCell>
                      <TableCell><TrailerAssociatedOrdersCell orders={orders} /></TableCell>
                      <TableCell><TrailerDocumentsCell trailer={trailer} /></TableCell>
                      <TableCell>
                        {trailer.current_location_status === "unknown" && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete trailer {trailer.trailer_id}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete this trailer and all its event history and comments. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteTrailerMutation.mutate(trailer.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                    {hasActiveFilter ? "No trailers match your filter criteria" : "No trailers found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
            }
          />
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={TRAILER_PAGE_SIZE_OPTIONS}
          />
        </div>
        </div>
      </div>

      <TrailerDetailDialog
        trailerId={selectedTrailer?.id ?? null}
        open={!!selectedTrailer}
        onOpenChange={(open) => { if (!open) setSelectedTrailer(null); }}
        showComments={false}
        showDelete
        onDelete={(id) => { deleteTrailerMutation.mutate(id); setSelectedTrailer(null); }}
        showCrossTenantWarning
      />
      </>
      )}
    </AppLayout>
  );
}
